import { create } from 'zustand';
import type { UserProfile } from '@/lib/api/types';

type Status = 'booting' | 'authenticated' | 'anonymous';

interface AuthState {
  status: Status;
  /** Kept in memory only (never localStorage) so injected scripts cannot persist it. */
  accessToken: string | null;
  user: UserProfile | null;
  sessionExpired: boolean;
  setSession: (token: string, user: UserProfile) => void;
  setUser: (user: UserProfile) => void;
  setAccessToken: (token: string | null) => void;
  expire: () => void;
  signOut: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'booting',
  accessToken: null,
  user: null,
  sessionExpired: false,
  setSession: (accessToken, user) => set({ accessToken, user, status: 'authenticated', sessionExpired: false }),
  setUser: (user) => set({ user }),
  setAccessToken: (accessToken) => set({ accessToken }),
  expire: () => set({ accessToken: null, user: null, status: 'anonymous', sessionExpired: true }),
  signOut: () => set({ accessToken: null, user: null, status: 'anonymous', sessionExpired: false }),
}));

/** Permission check mirroring the API: the owner holds everything. */
export function hasPermission(user: UserProfile | null, permission: string) {
  return !!user && (user.role === 'owner' || user.permissions.includes(permission));
}

export const useCan = () => {
  const user = useAuthStore((s) => s.user);
  return (permission: string) => hasPermission(user, permission);
};
